import { useTranslate } from '@refinedev/core';
import { Card, Descriptions, Grid, Space, Tag, Typography, theme } from 'antd';
import type { DescriptionsProps } from 'antd';
import type {
  PublicationAudioOut,
  PublicationDetailOut,
  PublicationEbookOut,
} from '@/api/generated/schemas';
import { HintLabel } from '@/components/common/HintLabel';
import { ShowLink } from '@/components/common/ShowLink';
import {
  formatDateTime,
  formatFileSizeComma,
  formatMinutesVerbose,
  formatThousands,
} from '@/utils/format';
import { publicationPublishingStatusColors } from '../constants';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';

const { Text, Link } = Typography;

interface PublicationFormatsTabProps {
  record: PublicationDetailOut;
}

type FormatKind = 'ebook' | 'audio';

const FormatCard = ({
  kind,
  data,
}: {
  kind: FormatKind;
  data: PublicationEbookOut | PublicationAudioOut | null | undefined;
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const verticalDescProps = useVerticalDescProps();
  const T = 'user_catalog.publications.show';
  const title = t(`user_catalog.publications.tabs.${kind}`);

  if (!data) {
    return (
      <Card
        style={{ borderStyle: 'dashed', height: '100%' }}
        styles={{
          body: {
            minHeight: 160,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      >
        <Text type="secondary">{t(`${T}.formats.no_${kind}`)}</Text>
      </Card>
    );
  }

  const ebook = kind === 'ebook' ? (data as PublicationEbookOut) : null;

  const links = (
    [
      ['link_product', data.product_url],
      ['link_demo', data.demo_url],
      ['link_preview', data.preview_url],
    ] as const
  ).filter(([, url]) => !!url);

  const items: DescriptionsProps['items'] = [
    {
      key: 'isbn',
      label: t('user_catalog.publications.ebook.fields.isbn'),
      children: orDash(data.isbn),
    },
    {
      key: 'ean',
      label: t('user_catalog.publications.ebook.fields.ean'),
      children: orDash(data.ean),
    },
    {
      key: 'edition',
      label: t('user_catalog.publications.ebook.fields.edition'),
      children: orDash(data.edition),
    },
    ...(ebook
      ? ([
          {
            key: 'pages_count',
            label: t('user_catalog.publications.ebook.fields.pages_count'),
            children: orDash(formatThousands(ebook.pages_count)),
          },
          {
            key: 'chars_count',
            label: t('user_catalog.publications.ebook.fields.chars_count'),
            children: orDash(formatThousands(ebook.chars_count)),
          },
        ] as NonNullable<DescriptionsProps['items']>)
      : []),
    {
      key: 'duration',
      label: (
        <HintLabel
          label={t(`${T}.fields.duration_time`)}
          hint={t(`${T}.fields.duration_time_hint_${kind}`)}
        />
      ),
      children: orDash(formatMinutesVerbose(data.duration_minutes)),
    },
    {
      key: 'duration_kind',
      label: t(`${T}.fields.duration`),
      children:
        data.duration_kind != null
          ? t(`user_catalog.publications.duration.${data.duration_kind}`)
          : orDash(null),
    },
    {
      key: 'files_size',
      label: t(`${T}.fields.files_size`),
      children: orDash(formatFileSizeComma(data.files_size_bytes)),
    },
    {
      key: 'source_id',
      label: t('user_catalog.publications.ebook.fields.source_id'),
      children:
        data.source_id != null ? (
          <ShowLink resource="content_catalog.products" id={data.source_id}>
            <Text code>{data.source_id}</Text>
          </ShowLink>
        ) : (
          orDash(null)
        ),
    },
    {
      key: 'lcps_content_id',
      label: t('user_catalog.publications.ebook.fields.lcps_content_id'),
      children: data.lcps_content_id ? (
        <Text code>{data.lcps_content_id}</Text>
      ) : (
        orDash(null)
      ),
    },
    {
      key: 'links',
      label: t(`${T}.fields.links`),
      children: links.length ? (
        <Space size={token.marginSM}>
          {links.map(([key, url]) => (
            <Link key={key} href={url ?? undefined} target="_blank">
              {t(`${T}.fields.${key}`)}
            </Link>
          ))}
        </Space>
      ) : (
        orDash(null)
      ),
    },
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(data.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(data.updated_at)),
    },
  ];

  return (
    <Card
      title={
        <Space size={10}>
          {title}
          <Tag
            color={
              publicationPublishingStatusColors[data.publishing_status] ??
              'default'
            }
          >
            {t(
              `user_catalog.publications.publishing_status.${data.publishing_status}`
            )}
          </Tag>
          <Tag>
            {t(`${T}.fields.moderation_tag`, {
              status: t(`base.mod_status.${data.mod_status}`),
            })}
          </Tag>
        </Space>
      }
      extra={
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          {t(`${T}.fields.id_version`, {
            id: data.id,
            version: data.version ?? '-',
          })}
        </Text>
      }
    >
      <Descriptions
        {...verticalDescProps}
        column={{ xs: 1, sm: 2 }}
        items={items}
      />
    </Card>
  );
};

export const PublicationFormatsTab = ({
  record,
}: PublicationFormatsTabProps) => {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: screens.lg ? '1fr 1fr' : '1fr',
        gap: token.margin,
        alignItems: 'start',
      }}
    >
      <FormatCard kind="ebook" data={record.ebook} />
      <FormatCard kind="audio" data={record.audio} />
    </div>
  );
};
